import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoTeams } from '../services/demoData';

const MobileNotifications = () => {
  const API_URL = getApiUrl();
  const [notifications, setNotifications] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  const getToken = () => localStorage.getItem('token');

  // Hent brukerens teams
  const fetchMyTeams = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      setMyTeams(demoTeams);
      return demoTeams;
    }
    
    try {
      const response = await fetch(`${API_URL}/teams/my-teams`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMyTeams(data);
        return data;
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Kunne ikke hente teams');
    }
    return [];
  };

  // Hent varsler for alle teams
  const fetchNotifications = async (teams) => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      setNotifications([
        { id: 1, message: 'Demo: Ny arbeidsordre AO-2024-001 opprettet', type: 'info', is_read: false, created_at: new Date().toISOString(), teamName: 'VANNSKJÆRING' },
        { id: 2, message: 'Demo: Status oppdatert til "I gang"', type: 'success', is_read: false, created_at: new Date(Date.now() - 3600000).toISOString(), teamName: 'SVEIS' },
        { id: 3, message: 'Demo: Leveringsdato nærmer seg', type: 'warning', is_read: true, created_at: new Date(Date.now() - 86400000).toISOString(), teamName: 'KANTPRESS' },
      ]);
      return;
    }
    
    try {
      const allNotifications = [];
      
      for (const team of teams) {
        const response = await fetch(
          `${API_URL}/notifications/team/${team.id}`,
          {
            headers: {
              'Authorization': `Bearer ${getToken()}`
            }
          }
        );

        if (response.ok) {
          const teamNotifications = await response.json();
          allNotifications.push(...teamNotifications.map(n => ({
            ...n,
            teamName: team.name
          })));
        }
      }

      // Sorter etter dato (nyeste først)
      allNotifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(allNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Kunne ikke hente varsler');
    }
  };

  // Hent antall uleste
  const fetchUnreadCount = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      setUnreadCount(2);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  // Marker som lest
  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(
        `${API_URL}/notifications/${notificationId}/read`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: 1 } : n)
        );
        await fetchUnreadCount();
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  // Marker alle som lest for et team
  const markAllAsRead = async (teamId) => {
    try {
      const response = await fetch(
        `${API_URL}/notifications/team/${teamId}/read-all`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.team_id === teamId ? { ...n, is_read: 1 } : n)
        );
        await fetchUnreadCount();
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Slett en notifikasjon
  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(
        `${API_URL}/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        await fetchUnreadCount();
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  // Slett alle varsler for et team
  const deleteAllForTeam = async (teamId) => {
    try {
      const response = await fetch(
        `${API_URL}/notifications/team/${teamId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.team_id !== teamId));
        await fetchUnreadCount();
      }
    } catch (err) {
      console.error('Error deleting all notifications:', err);
    }
  };

  // Last inn data
  const loadData = async () => {
    setLoading(true);
    setError('');
    const teams = await fetchMyTeams();
    if (teams.length > 0) {
      await fetchNotifications(teams);
      await fetchUnreadCount();
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // Oppdater hver 30. sekund
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filtrer varsler basert på valgt tab
  const getFilteredNotifications = () => {
    if (selectedTab === 0) {
      // Alle varsler
      return notifications;
    } else if (selectedTab === 1) {
      // Kun uleste
      return notifications.filter(n => !n.is_read);
    }
    return notifications;
  };

  // Ikon basert på type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <WarningIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  // Formater tidspunkt
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Ukjent dato';
    
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Ukjent dato';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Nå';
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} timer siden`;
    if (diffDays < 7) return `${diffDays} dager siden`;
    return date.toLocaleDateString('nb-NO');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredNotifications = getFilteredNotifications();

  return (
    <Box>
      {/* Header med tabs */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon color="primary" fontSize="large" />
              </Badge>
              <Typography variant="h6" fontWeight="bold">
                Varsler
              </Typography>
            </Box>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>

          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="fullWidth"
          >
            <Tab label={`Alle (${notifications.length})`} />
            <Tab label={`Uleste (${unreadCount})`} />
          </Tabs>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Liste over varsler */}
      {filteredNotifications.length === 0 ? (
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <NotificationsIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {selectedTab === 1 ? 'Ingen uleste varsler' : 'Ingen varsler'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Du er oppdatert!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card elevation={1}>
          <List>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: notification.is_read ? 'grey.300' : 'primary.main' }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 4 }}>
                        <Typography variant="subtitle2" fontWeight={notification.is_read ? 'normal' : 'bold'}>
                          {notification.message}
                        </Typography>
                        {!notification.is_read && (
                          <Chip label="Ny" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {notification.teamName}
                        </Typography>
                        {notification.created_at && !isNaN(new Date(notification.created_at).getTime()) && (
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(notification.created_at)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Marker alle som lest-knapp */}
      {unreadCount > 0 && myTeams.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {myTeams.map(team => {
            const teamUnreadCount = notifications.filter(
              n => n.team_id === team.id && !n.is_read
            ).length;
            
            if (teamUnreadCount === 0) return null;
            
            return (
              <Button
                key={team.id}
                variant="outlined"
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={() => markAllAsRead(team.id)}
              >
                Marker alle i {team.name} som lest ({teamUnreadCount})
              </Button>
            );
          })}
        </Box>
      )}

      {/* Slett alle varsler-knapper */}
      {notifications.length > 0 && myTeams.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {myTeams.map(team => {
            const teamNotificationCount = notifications.filter(
              n => n.team_id === team.id
            ).length;
            
            if (teamNotificationCount === 0) return null;
            
            return (
              <Button
                key={`delete-${team.id}`}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={() => deleteAllForTeam(team.id)}
              >
                Slett alle i {team.name} ({teamNotificationCount})
              </Button>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MobileNotifications;
